import { FaMapPin, FaClock } from 'react-icons/fa6'
import SectionSkipLink from '@/components/ui/SectionSkipLink'

function LocationSection() {
  return (
    <section
      id="location"
      className="relative flex h-auto items-center justify-center bg-bhm-neutral-100 py-16 text-black md:py-28 dark:bg-bhm-neutral-700 dark:text-white"
      aria-labelledby="location-heading"
    >
      <div className="mx-auto flex w-full max-w-[1200px] flex-col space-y-8 px-4 sm:px-6 lg:px-8">
        {/* When Section */}
        <div className="flex justify-center">
          <div className="flex items-center">
            <div className="mx-2 flex size-11 shrink-0 items-center justify-center rounded-full bg-yellow-500">
              <FaClock
                className="size-6 shrink-0 text-white"
                aria-hidden="true"
              />
            </div>
            <h2
              id="location-heading"
              className="font-biorhyme text-5xl text-bhm-neutral-900 md:text-5xl lg:text-6xl dark:text-bhm-neutral-50"
            >
              When & Where
            </h2>
          </div>
        </div>
        <div className="flex justify-center pb-16">
          <div className="text-center">
            <div className="mt-2 space-y-1">
              <time
                dateTime="2026-02-28T08:00:00-05:00/2026-02-28T18:00:00-05:00"
                className="block text-xl font-semibold"
              >
                Saturday, February 28th: 8:00 AM - 6:00 PM
              </time>
            </div>
          </div>
        </div>

        {/* Saturday Location Row - center both columns on mobile (<1024), side-by-side on lg */}
        <div className="flex w-full max-w-full flex-col items-center lg:flex-row lg:items-stretch lg:space-x-6">
          {/* Saturday Location Info - centered on mobile, left on lg */}
          <div className="mb-4 flex flex-col items-center lg:mb-0 lg:w-1/2 lg:items-start">
            <div className="mb-4 flex items-center justify-center lg:justify-start">
              <div className="mx-2 flex size-11 shrink-0 items-center justify-center rounded-full bg-yellow-500">
                <FaMapPin
                  className="size-6 shrink-0 text-white"
                  aria-hidden="true"
                />
              </div>
              <dl>
                <dt className="sr-only">Venue</dt>
                <dd className="m-0">
                  <h3 className="text-center text-3xl font-semibold lg:text-left">
                    DTE Energy HQ
                  </h3>
                </dd>
              </dl>
            </div>

            <dl className="flex w-full flex-col items-center lg:items-start">
              <dt className="sr-only">Address</dt>
              <dd>
                <address
                  className="mt-1 text-center not-italic lg:ml-16 lg:text-left xl:whitespace-nowrap"
                  aria-label="Main event venue address"
                >
                  1 Energy Plaza, Detroit, MI 48226
                </address>
              </dd>
            </dl>

            <div className="my-4 flex justify-center lg:justify-start">
              <a
                href="https://www.google.com/maps/dir//DTE+Energy+Headquarters+1+Energy+Plaza+Detroit,+48226"
                target="_blank"
                className="inline-flex w-fit items-center whitespace-nowrap rounded bg-bhm-gold-300 px-8 py-5 font-bold text-bhm-neutral-950 shadow-xl transition delay-75 duration-100 ease-in-out hover:-translate-y-1 hover:scale-110 hover:cursor-pointer focus:outline-none focus:ring-2 focus:ring-focus-ring focus:ring-offset-2 lg:ml-16"
                rel="noreferrer"
                aria-label="Get directions to DTE Energy Headquarters (opens in new tab)"
              >
                GET DTE DIRECTIONS
              </a>
            </div>

            <dl className="flex w-full flex-col items-center lg:items-start">
              <dt className="sr-only">Free parking</dt>
              <dd className="mt-8 flex items-center justify-center lg:justify-start">
                <div className="mx-2 flex size-11 shrink-0 items-center justify-center rounded-full bg-yellow-500">
                  <FaMapPin
                    className="size-6 shrink-0 text-white"
                    aria-hidden="true"
                  />
                </div>
                <h3 className="text-center text-2xl font-semibold lg:text-left">
                  MGM Grand Detroit Self Parking (FREE)
                </h3>
              </dd>
              <dd className="mt-4 flex flex-col items-center lg:items-start">
                <address
                  className="text-center not-italic lg:ml-16 lg:text-left xl:whitespace-nowrap"
                  aria-label="Free parking address"
                >
                  2199 3rd Ave, Detroit, MI 48226
                </address>
                <a
                  href="https://bit.ly/mgm-self-parking"
                  target="_blank"
                  rel="noreferrer"
                  className="my-4 inline-flex w-fit items-center whitespace-nowrap rounded border-4 border-bhm-neutral-950 bg-bhm-gold-200 px-8 py-5 font-bold text-bhm-neutral-950 shadow-lg transition delay-75 duration-100 ease-in-out hover:-translate-y-1 hover:scale-110 hover:cursor-pointer hover:bg-bhm-gold-50 focus:outline-none focus:ring-2 focus:ring-focus-ring focus:ring-offset-2 lg:ml-16 dark:border-bhm-gold-200 dark:bg-transparent dark:text-bhm-neutral-50 dark:hover:bg-transparent"
                  aria-label="Information about parking for free (opens in new tab)"
                >
                  FREE PARKING &mdash; INFO
                </a>
              </dd>
              <dd className="mt-4 flex justify-center lg:ml-16 lg:justify-start">
                <ul className="w-fit list-inside list-disc rounded-lg border-2 border-dashed border-bhm-neutral-400 p-4 font-semibold text-bhm-neutral-900 dark:border-bhm-neutral-500 dark:text-bhm-neutral-100">
                  <li>Park on Floors 2-5</li>
                  <li>Use SE elevators closest to Third Ave</li>
                  <li>Enter DTE on Floor 2</li>
                </ul>
              </dd>
            </dl>

            <div className="relative">
              <SectionSkipLink href="#schedule">
                Skip location section
              </SectionSkipLink>
            </div>
          </div>

          {/* Saturday Map - centered on mobile, left on lg */}
          <div className="mx-2 flex w-full max-w-full flex-col items-center overflow-hidden lg:w-1/2 lg:items-stretch">
            <h4 className="mb-2 text-center text-sm font-semibold text-gray-700 lg:text-left dark:text-white">
              Saturday - Free Parking
            </h4>
            <p className="mb-2 text-center text-lg font-semibold lg:text-left">
              MGM Grand Detroit Self Parking
            </p>
            <div
              className="rounded-2xl border-4 border-blue-500 bg-white p-1"
              role="region"
              aria-label="Saturday location map"
            >
              <iframe
                title="MGM Grand Detroit Self Parking"
                aria-label="Map showing MGM Grand Detroit Self Parking location"
                className="aspect-[4/3] rounded-xl border-4 border-white"
                style={{
                  maxWidth: '100%',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d737.3372447776826!2d-83.06011360377427!3d42.33508251131216!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x883b2d49be82ef77%3A0xfe82f1372017dff8!2s2199%203rd%20Ave%2C%20Detroit%2C%20MI%2048226!5e0!3m2!1sen!2sus!4v1772077742474!5m2!1sen!2sus"
                allowFullScreen
                loading="lazy"
                sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              >
                <p>
                  Interactive map showing MGM Grand Detroit Self Parking
                  location.
                  <a
                    href="https://bit.ly/mgm-self-parking"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in Google Maps
                  </a>
                </p>
              </iframe>
            </div>
          </div>
        </div>

        {/* Lumen Detroit Row - center on mobile (<1024), left on lg */}
        <div className="flex w-full flex-col items-center lg:items-start">
          <dl className="flex w-full flex-col items-center lg:items-start">
            <dt className="sr-only">Tech Hour &amp; Networking venue</dt>
            <dd className="flex items-center justify-center lg:justify-start">
              <div className="mx-2 flex size-11 shrink-0 items-center justify-center rounded-full bg-yellow-500">
                <FaMapPin
                  className="size-6 shrink-0 text-white"
                  aria-hidden="true"
                />
              </div>
              <h3 className="text-2xl font-semibold">Lumen Detroit</h3>
            </dd>
            <dd className="mt-4 flex flex-col items-center lg:items-start">
              <address
                className="text-center not-italic lg:ml-16 lg:text-left xl:whitespace-nowrap"
                aria-label="Lumen Detroit address"
              >
                1903 Grand River Ave, Detroit, MI 48226
              </address>

              <a
                href="https://www.google.com/maps/dir/?api=1&origin=1+Energy+Plaza,+Detroit,+MI+48226&destination=Lumen+Detroit,+1903+Grand+River+Ave,+Detroit,+MI+48226&travelmode=walking"
                target="_blank"
                rel="noreferrer"
                className="my-4 inline-flex w-fit items-center font-bold text-bhm-red-700 underline underline-offset-4 hover:text-bhm-red-900 focus:outline-none focus:ring-2 focus:ring-focus-ring focus:ring-offset-2 lg:ml-16 dark:text-bhm-gold-200 dark:hover:text-bhm-gold-400"
                aria-label="Get walking directions to Lumen Detroit (opens in new tab)"
              >
                GET LUMEN DIRECTIONS
              </a>
            </dd>
          </dl>
        </div>
      </div>
    </section>
  )
}

export default LocationSection
